import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../types/commands';
import fetchService from '../../../services/fetchService';
import { API_BASE_URL } from '../../../api/config';
import { schemaToParameters } from '../../../utils/schemaToParameters';

export const email_read: SimpleDocCommand = {
  id: 'email_read',
  logicalName: 'email_read',
  version: 'v1',

  description: `Search and read emails from connected accounts (Gmail, Outlook, IMAP).

**FILTERS:**
- query: Full-text search across subject, body, and sender
- from: Sender email address
- subject: Email subject line
- folder: Folder/label (INBOX, Sent, Drafts, etc.)
- startDate/endDate: Date range in ISO 8601 format (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS)
- isRead: Filter by read status (true = read, false = unread)
- limit: Max emails to return (default 20, max 50)
- integration_id: (optional) Scope to a specific connected account. When omitted, searches across all accounts.

**EXAMPLES:**

Read recent emails from a specific sender:
{ "from": "boss@company.com", "startDate": "2026-02-01", "limit": 30 }

Search for unread emails in inbox:
{ "folder": "INBOX", "isRead": false }

Search by subject keyword:
{ "query": "contract renewal", "limit": 10 }

Search in a specific account (multi-account):
{ "query": "invoice", "integration_id": "<integration_id>" }

**RETURNS:** Array of emails with subject, from, date, body (truncated), folder, isRead`,

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        query:          { type: 'string',  description: 'Full-text search query across subject, body, and sender' },
        from:           { type: 'string',  description: 'Filter by sender email address' },
        subject:        { type: 'string',  description: 'Filter by email subject' },
        folder:         { type: 'string',  description: 'Filter by folder/label (INBOX, Sent, Drafts, etc.)' },
        startDate:      { type: 'string',  description: 'Filter by start date (ISO 8601: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS)' },
        endDate:        { type: 'string',  description: 'Filter by end date (ISO 8601 format)' },
        isRead:         { type: 'boolean', description: 'Filter by read status (true = read, false = unread)' },
        limit:          { type: 'number',  description: 'Max emails to return (default 20, max 50)' },
        integration_id: { type: 'string',  description: 'Scope search to a specific connected account. Omit to search all accounts.' },
      },
      required: [],
    }),
  },

  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.status === 'success' && result?.data) {
        const data = result.data;
        return {
          type: 'email_results',
          summary: result.message || `Found ${data.count || 0} emails`,
          data: {
            emails: data.emails || [],
            count: data.count || 0,
            total_count: data.total_count || data.count || 0,
            truncated: data.truncated || false,
            account: data.account,
            query: toolInput?.query
          },
          meta: {
            searchParams: {
              query: toolInput?.query,
              from: toolInput?.from,
              subject: toolInput?.subject,
              folder: toolInput?.folder
            }
          }
        };
      }
      return {
        type: 'tool_summary',
        summary: result?.message || 'Email search completed',
        data: result?.data || {}
      };
    } catch (error) {
      console.warn('[email_read] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'Email search completed',
        data: {}
      };
    }
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { query, from, subject, folder, startDate, endDate, isRead, semantic, limit = 20, integration_id } = params;

    try {
      const numericLimit = typeof limit === 'number' && !isNaN(limit) ? limit : 20;
      const safeLimit = Math.min(Math.max(1, numericLimit), 100);

      const body: Record<string, unknown> = { limit: safeLimit };
      if (query)             body.query          = query;
      if (from)              body.from_address   = from;
      if (subject)           body.subject        = subject;
      if (folder)            body.folder         = folder;
      if (startDate)         body.start_date     = startDate;
      if (endDate)           body.end_date       = endDate;
      if (isRead !== undefined) body.is_read     = isRead;
      if (semantic)          body.semantic       = true;
      if (integration_id)    body.integration_id = integration_id;

      console.info('[email_read] multi-account search', {
        integrationId: integration_id ?? 'all',
        hasQuery: !!query,
      });

      const data = await fetchService(`${API_BASE_URL}/mail/search`, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      const threads = Array.isArray(data?.threads) ? data.threads : [];

      if (threads.length === 0) {
        return {
          status: 'success',
          message: 'No emails found matching criteria',
          data: { threads: [], count: 0 },
          format: 'json',
        };
      }

      return {
        status: 'success',
        message: `Found ${threads.length} email thread${threads.length > 1 ? 's' : ''}`,
        data: { threads, count: threads.length, truncated: threads.length >= safeLimit },
        format: 'json',
      };
    } catch (error: any) {
      console.error('[email_read] Error:', error);

      let errorMessage = 'Failed to search emails';
      let errorDetails = error.message;
      if (error.data?.detail) {
        errorDetails = error.data.detail;
        errorMessage = errorDetails;
      }

      const errorCategory =
        error.status === 400 ? 'validation' :
        error.status === 403 ? 'permission' :
        error.status === 404 ? 'validation' : 'execution';

      return {
        status: 'error',
        message: errorMessage,
        error: {
          code: error.status ? `http_${error.status}` : 'unknown',
          category: errorCategory as 'execution' | 'validation' | 'permission',
          details: errorDetails,
        },
        format: 'plaintext',
      };
    }
  }
};
